/*
 * Date Format 1.2.3
 * (c) 2007-2009 Steven Levithan <stevenlevithan.com>
 * MIT license
 *
 * Includes enhancements by Scott Trenda <scott.trenda.net>
 * and Kris Kowal <cixar.com/~kris.kowal/>
 *
 * Accepts a date, a mask, or a date and a mask.
 * Returns a formatted version of the given date.
 * The date defaults to the current date/time.
 * The mask defaults to dateFormat.masks.default.
 *
 * https://github.com/felixge/node-dateformat
 */

'use strict';
export class DF {

    constructor() {
        this.token = /{|}|y{1,4}|d{1,4}|M{1,4}|m{1,4}|f{1,3}|F{1,3}|z{1,3}|Z{1,3}|K{1}|g{1,2}|([HhMsTt])\1?|[oSZWN]|"[^"]*"|'[^']*'/g;
        this.timezone = /\b(?:[PMCEA][SDP]T|(?:Pacific|Mountain|Central|Eastern|Atlantic) (?:Standard|Daylight|Prevailing) Time|(?:GMT|UTC)(?:[-+]\d{4})?)\b/g;
        this.timezoneClip = /[^-+\dA-Z]/g;

        this.masks = {
            'default': 'dd.MM.yyyy HH:mm:ss',
            'shortDate': 'm/d/yy',
            'mediumDate': 'mmm d, yyyy',
            'longDate': 'mmmm d, yyyy',
            'fullDate': 'dddd, mmmm d, yyyy',
            'shortTime': 'h:MM TT',
            'mediumTime': 'h:MM:ss TT',
            'longTime': 'h:MM:ss TT Z',
            'isoDate': 'yyyy-mm-dd',
            'isoTime': 'HH:MM:ss',
            'isoDateTime': 'yyyy-mm-dd\'T\'HH:MM:sso',
            'isoUtcDateTime': 'UTC:yyyy-mm-dd\'T\'HH:MM:ss\'Z\'',
            'expiresHeaderFormat': 'ddd, dd mmm yyyy HH:MM:ss Z'
        };

        this.i18n = {
            dayNamesEN: [
                'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat',
                'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
            ],
            monthNamesEN: [
                'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
                'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
            ],
            dayNamesRU: [
                'Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб',
                'Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'
            ],
            monthNamesRU: [
                'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек',
                'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
            ],
            timeNames: [
                'a', 'p', 'am', 'pm', 'A', 'P', 'AM', 'PM'
            ]
        };
    }

    // Метод будет форматировать миллисекунды в формат "HH:mm:ss" или "d.HH:mm:ss"
    dateFormatTimeSpan(timeMs) {
        if (!timeMs) {
            return "00:00:00";
        }
        const msInDay = 86400000;
        const daysCount = Math.floor(timeMs / msInDay);
        const time = this.dateFormat(
            // 24 часа через new Date будет отобожать 2 дня, т.е.: 2.00:00:00, а нужно отображать 1.00:00:00.
            // Чтобы этого избежать нужно вычитать 24 часа в ms из переданной даты, если она больше 24 часов.
            new Date(timeMs >= msInDay ? timeMs - daysCount * msInDay : timeMs),
            "HH:mm:ss",
            true
        );
        return daysCount ? `${daysCount}.${time}` : time;
    }

    // Regexes and supporting functions are cached through closure
    dateFormat(date, mask, utc, gmt) {
        if (!date) {
            return "";
        }

        if (!(date instanceof Date)) {
            date = new Date(date);
        }

        if (isNaN(date)) {
            throw TypeError('Invalid date');
        }

        mask = String(this.masks[mask] || mask || this.masks['default']);

        // Allow setting the utc/gmt argument via the mask
        const maskSlice = mask.slice(0, 4);
        if (maskSlice === 'UTC:' || maskSlice === 'GMT:') {
            mask = mask.slice(4);
            utc = true;
            if (maskSlice === 'GMT:') {
                gmt = true;
            }
        }

        const _ = utc ? 'getUTC' : 'get';
        const d = date[_ + 'Date']();
        const D = date[_ + 'Day']();
        const m = date[_ + 'Month']();
        const y = date[_ + 'FullYear']();
        const H = date[_ + 'Hours']();
        const M = date[_ + 'Minutes']();
        const s = date[_ + 'Seconds']();
        const L = date[_ + 'Milliseconds']();
        const o = utc ? 0 : date.getTimezoneOffset();
        const W = this.getWeek(date);
        const N = this.getDayOfWeek(date);
        const oneF = this.pad(Math.round(L / 100), 1);
        const twoF = this.pad(Math.round(L / 10), 2);
        const threeF = this.pad(L, 3);
        const zzz = (o > 0 ? '-' : '+') + this.pad(Math.floor(Math.abs(o) / 60)) + ':' + this.pad(Math.abs(o) % 60);
        const flags = {
            g: 'A.D',
            gg: 'A.D',
            d: d,
            dd: this.pad(d),
            ddd: window._globalParams.Language.toLowerCase() === 'ru' ? this.i18n.dayNamesRU[D] : this.i18n.dayNamesEN[D],
            dddd: window._globalParams.Language.toLowerCase() === 'ru' ? this.i18n.dayNamesRU[D + 7] : this.i18n.dayNamesEN[D + 7],
            M: m + 1,
            MM: this.pad(m + 1),
            MMM: window._globalParams.Language.toLowerCase() === 'ru' ? this.i18n.monthNamesRU[m] : this.i18n.monthNamesEN[m],
            MMMM: window._globalParams.Language.toLowerCase() === 'ru' ? this.i18n.monthNamesRU[m + 12] : this.i18n.monthNamesEN[m + 12],
            y: String(y).slice(3),
            yy: String(y).slice(2),
            yyy: String(y).slice(1),
            yyyy: y,
            h: H % 12 || 12,
            hh: this.pad(H % 12 || 12),
            H: H,
            HH: this.pad(H),
            m: M,
            mm: this.pad(M),
            s: s,
            ss: this.pad(s),
            f: oneF,
            ff: twoF,
            fff: threeF,
            F: oneF,
            FF: twoF,
            FFF: threeF,
            t: H < 12 ? this.i18n.timeNames[0] : this.i18n.timeNames[1],
            tt: H < 12 ? this.i18n.timeNames[2] : this.i18n.timeNames[3],
            T: H < 12 ? this.i18n.timeNames[4] : this.i18n.timeNames[5],
            TT: H < 12 ? this.i18n.timeNames[6] : this.i18n.timeNames[7],
            Z: gmt ? 'GMT' : utc ? 'UTC' : (String(date).match(this.timezone) || ['']).pop().replace(this.timezoneClip, ''),
            o: (o > 0 ? '-' : '+') + this.pad(Math.floor(Math.abs(o) / 60) * 100 + Math.abs(o) % 60, 4),
            S: ['th', 'st', 'nd', 'rd'][d % 10 > 3 ? 0 : (d % 100 - d % 10 != 10) * d % 10],
            W: W,
            N: N,
            z: (o > 0 ? '-' : '+') + Math.floor(Math.abs(o) / 60),
            zz: (o > 0 ? '-' : '+') + this.pad(Math.floor(Math.abs(o) / 60)),
            zzz: zzz,
            K: zzz
        };

        return mask.replace(this.token, match => {
            if (match in flags) {
                return flags[match];
            }
            return match.slice(1, match.length - 1);
        });
    };




    pad(val, len) {
        val = String(val);
        len = len || 2;
        while (val.length < len) {
            val = '0' + val;
        }
        return val;
    }

    /**
     * Get the ISO 8601 week number
     * Based on comments from
     * http://techblog.procurios.nl/k/n618/news/view/33796/14863/Calculate-ISO-8601-week-and-year-in-javascript.html
     *
     * @param  {Object} `date`
     * @return {Number}
     */
    getWeek(date) {
        // Remove time components of date
        const targetThursday = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        // Change date to Thursday same week
        targetThursday.setDate(targetThursday.getDate() - ((targetThursday.getDay() + 6) % 7) + 3);

        // Take January 4th as it is always in week 1 (see ISO 8601)
        const firstThursday = new Date(targetThursday.getFullYear(), 0, 4);

        // Change date to Thursday same week
        firstThursday.setDate(firstThursday.getDate() - ((firstThursday.getDay() + 6) % 7) + 3);

        // Check if daylight-saving-time-switch occurred and correct for it
        const ds = targetThursday.getTimezoneOffset() - firstThursday.getTimezoneOffset();
        targetThursday.setHours(targetThursday.getHours() - ds);

        // Number of weeks between target Thursday and first Thursday
        const weekDiff = (targetThursday - firstThursday) / (86400000 * 7);
        return 1 + Math.floor(weekDiff);
    }

    /**
     * Get ISO-8601 numeric representation of the day of the week
     * 1 (for Monday) through 7 (for Sunday)
     *
     * @param  {Object} `date`
     * @return {Number}
     */
    getDayOfWeek(date) {
        let dow = date.getDay();
        if (dow === 0) {
            dow = 7;
        }
        return dow;
    }
}
